import React from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import RTKQErrorCard from "../../../../common/components/RTKQErrorCard";
import type { RTKQueryError } from "../../../../common/components/RTKQErrorCard";
// Import the view component
import OrderSummary from "./OrderSummary";
// Import cart query hook to get cart data
import { useGetCartQuery } from "../../../cart/api/cartApi"; // Corrected path

const OrderSummaryContainer: React.FC = () => {
  const { t, ready: translationsReady } = useTranslation(); // Add translationsReady
  // Fetch cart data. isFetching helps show loading during background refetches.
  const {
    data: cart,
    isLoading: isCartLoading,
    error,
    isFetching, // Use isFetching for background updates
    isError, // Use isError for permanent fetch errors
  } = useGetCartQuery(undefined, {
    // Options to consider:
    refetchOnMountOrArgChange: true, // Refetch when component mounts
    refetchOnFocus: true, // Refetch when window regains focus
  });

  const isLoading = isCartLoading || !translationsReady; // Combine loading states

  // --- Loading State ---
  if (isLoading) {
    return (
      <div className="checkout-order-summary checkout-order-summary--loading">
        <h2>
          <Skeleton width={150} height={24} />
        </h2>
        {/* Skeleton for items */}
        <div className="summary-items-list" style={{ marginBottom: "1.5rem" }}>
          {[...Array(2)].map((_, index) => (
            <div
              key={index}
              className="summary-item skeleton-item"
              style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}
            >
              <Skeleton width={60} height={60} />
              <div style={{ flex: 1 }}>
                <Skeleton height={18} width="80%" />
                <Skeleton
                  height={14}
                  width="50%"
                  style={{ marginTop: "0.25rem" }}
                />
              </div>
              <Skeleton height={18} width={60} />
            </div>
          ))}
        </div>
        {/* Skeleton for totals */}
        <div className="summary-totals">
          <Skeleton
            height={20}
            width="90%"
            style={{ marginBottom: "0.75rem" }}
          />
          <Skeleton height={20} width="80%" style={{ marginBottom: "1rem" }} />
          <Skeleton height={24} width="100%" />
        </div>
      </div>
    );
  }

  // --- Error State ---
  // Show error only if it's a permanent error (isError), not just background refetch failure
  if (isError && !cart) {
    // Check !cart to avoid showing error if we have stale data
    return (
      <div className="checkout-order-summary checkout-order-summary--error">
        <h2>{t("checkout.orderSummary", "Your Order")}</h2>
        <RTKQErrorCard
          error={error as RTKQueryError | null} // Cast error
          title={t("cart.errors.loadFailed", "Failed to load cart")}
          compact={true}
        />
      </div>
    );
  }

  // --- Cart Empty or Not Found State ---
  // If loading is done, no error, but cart is still null/undefined or has no items
  if (!cart || cart.itemsCount === 0) {
    return (
      <div className="checkout-order-summary checkout-order-summary--empty">
        <h2>{t("checkout.orderSummary", "Your Order")}</h2>
        <p>{t("checkout.summaryEmptyCart", "Your cart is empty.")}</p>
        {/* Optionally add a link back to shop */}
        <a
          href="/shop"
          className="button empty-cart-shop-link"
          style={{ marginTop: "1rem", display: "inline-block" }}
        >
          {t("cart.continueShopping", "Continue Shopping")}
        </a>
      </div>
    );
  }

  // --- Render Order Summary View ---
  // Pass the fetched cart data to the view component
  return <OrderSummary cart={cart} />;
};

export default OrderSummaryContainer;
